<template>
  <div
    class="fixed inset-0 z-[2000] flex items-center justify-center bg-black/40 p-4"
    @click.self="$emit('close')"
  >
    <div
      class="bg-white dark:bg-gray-800 w-[98vw] max-w-[1600px] h-[90vh] rounded-lg shadow-lg overflow-hidden flex flex-col"
    >
      <div
        class="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between"
      >
        <div>
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Uren & Assignments</h3>
          <p class="text-sm text-gray-500 dark:text-gray-400">{{ employee?.name }}</p>
          <div class="text-sm text-gray-600 dark:text-gray-400">
            {{ parseFloat(employee?.fte_ratio) || 1.0 }}FTE •
            {{ parseFloat(employee?.contractual_hours_per_week) || 40 }}u/week contract
          </div>
        </div>
        <button
          @click="$emit('close')"
          class="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          ✕
        </button>
      </div>
      <div class="p-4 sm:p-6 flex-1 overflow-hidden min-h-0">
        <div
          class="flex flex-col sm:flex-row gap-4 sm:gap-6 h-full overflow-y-auto sm:overflow-hidden"
        >
          <!-- Left Column: Bar Chart + Week Details -->
          <div class="flex-1 flex flex-col space-y-4 sm:space-y-6 sm:overflow-y-auto min-w-0">
            <!-- Period Chart -->
            <div>
              <h4
                class="text-md font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2"
              >
                <svg
                  class="h-5 w-5 text-indigo-600 dark:text-indigo-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M3 3v18h18M7 13l3 3 7-7"
                  />
                </svg>
                Uren over periode
                <span class="text-xs font-normal text-gray-500 dark:text-gray-400 ml-2"
                  >(klik op een week voor details)</span
                >
              </h4>
              <EmployeeHoursBarChart
                :employee="employee"
                :period-start="periodStart"
                :period-end="periodEnd"
                :extra-proposed-hours-by-week="extraProposedHoursByWeek"
                unit="hours"
                height="280px"
                @week-click="handleWeekClick"
              />
            </div>

            <!-- Selected Week Details -->
            <div
              class="border-2 border-blue-200 dark:border-blue-800 rounded-lg bg-blue-50 dark:bg-blue-900/30 p-4"
            >
              <div class="flex items-center justify-between mb-3">
                <h4
                  class="text-md font-semibold text-blue-900 dark:text-blue-200 flex items-center gap-2"
                >
                  <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  {{ selectedWeek?.iso_week || 'Week' }} ({{
                    formatDateShort(selectedWeek?.week_start)
                  }}
                  - {{ formatDateShort(selectedWeek?.week_end) }})
                </h4>
              </div>

              <!-- Loading selected week -->
              <div v-if="loadingSelectedWeek" class="text-center py-4">
                <div
                  class="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 dark:border-blue-400 mx-auto"
                ></div>
                <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">Uren laden…</p>
              </div>

              <!-- Selected week entries -->
              <div v-else-if="selectedWeekEntries.length > 0">
                <table class="min-w-full text-sm">
                  <thead>
                    <tr class="border-b border-blue-200 dark:border-blue-700">
                      <th class="text-left py-2 px-2 font-medium text-blue-900 dark:text-blue-200">
                        Datum
                      </th>
                      <th class="text-left py-2 px-2 font-medium text-blue-900 dark:text-blue-200">
                        Type
                      </th>
                      <th class="text-left py-2 px-2 font-medium text-blue-900 dark:text-blue-200">
                        Project
                      </th>
                      <th class="text-right py-2 px-2 font-medium text-blue-900 dark:text-blue-200">
                        Uren
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(row, idx) in selectedWeekEntries"
                      :key="idx"
                      class="border-b border-blue-100 dark:border-blue-800 last:border-0"
                    >
                      <td class="py-2 px-2 text-gray-700 dark:text-gray-300">
                        {{ formatDate(row.start_date) }}
                      </td>
                      <td class="py-2 px-2">
                        <span
                          class="px-2 py-0.5 rounded text-xs font-medium"
                          :style="{
                            backgroundColor: getLabelBgColor(row.type_label),
                            color: getLabelTextColor(row.type_label)
                          }"
                        >
                          {{ row.type_label || 'Overig' }}
                        </span>
                      </td>
                      <td class="py-2 px-2 text-gray-700 dark:text-gray-300">
                        <a
                          v-if="row.project_simplicate_id && row.type_label === 'Projectmanagement'"
                          :href="getSimplicateProjectUrl(row.project_simplicate_id)"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 hover:underline"
                        >
                          {{ formatProjectDisplay(row) }}
                        </a>
                        <span v-else>{{ formatProjectDisplay(row) }}</span>
                      </td>
                      <td class="py-2 px-2 text-right font-semibold text-gray-900 dark:text-white">
                        <div>{{ effectiveHours(row).toFixed(1) }}u</div>
                        <div
                          v-if="Number(row.correction_amount || 0) !== 0"
                          class="text-xs font-normal text-amber-600 dark:text-amber-400"
                        >
                          correctie {{ formatSignedHours(row.correction_amount) }}u
                        </div>
                      </td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr class="border-t-2 border-blue-300 dark:border-blue-600">
                      <td
                        colspan="3"
                        class="py-2 px-2 font-semibold text-blue-900 dark:text-blue-200"
                      >
                        Totaal uren
                      </td>
                      <td class="py-2 px-2 text-right font-bold text-blue-900 dark:text-blue-200">
                        {{ selectedWeekTotal.toFixed(1) }}u
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div v-else class="text-center text-gray-600 dark:text-gray-400 py-4 text-sm">
                Geen uren geboekt in deze week.
              </div>

              <!-- Voorgesteld (proposed) for selected week -->
              <div
                v-if="selectedWeekProposed.length > 0"
                class="mt-4 pt-4 border-t border-blue-200 dark:border-blue-700"
              >
                <h5
                  class="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-2 flex items-center gap-2"
                >
                  <span
                    class="inline-block h-2.5 w-2.5 rounded-full bg-yellow-400 ring-1 ring-yellow-600"
                  />
                  Voorgesteld deze week
                </h5>
                <table class="min-w-full text-sm">
                  <thead>
                    <tr class="border-b border-blue-200 dark:border-blue-700">
                      <th class="text-left py-2 px-2 font-medium text-blue-900 dark:text-blue-200">
                        Project
                      </th>
                      <th class="text-left py-2 px-2 font-medium text-blue-900 dark:text-blue-200">
                        Rol
                      </th>
                      <th class="text-right py-2 px-2 font-medium text-blue-900 dark:text-blue-200">
                        DPW
                      </th>
                      <th class="text-right py-2 px-2 font-medium text-blue-900 dark:text-blue-200">
                        Uren
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(proposal, idx) in selectedWeekProposed"
                      :key="idx"
                      class="border-b border-blue-100 dark:border-blue-800 last:border-0"
                    >
                      <td class="py-2 px-2 text-gray-700 dark:text-gray-300">
                        {{ proposal.prospect_subject || proposal.organization_name || '—' }}
                        <span
                          v-if="proposal.organization_name && proposal.prospect_subject"
                          class="text-gray-500 dark:text-gray-400"
                        >
                          - {{ proposal.organization_name }}</span
                        >
                        <span
                          v-if="proposal.is_local"
                          class="ml-1 text-[10px] uppercase tracking-wide text-yellow-700 dark:text-yellow-300"
                          >concept</span
                        >
                      </td>
                      <td class="py-2 px-2 text-yellow-700 dark:text-yellow-300">
                        {{ proposal.role || '—' }}
                      </td>
                      <td class="py-2 px-2 text-right text-gray-700 dark:text-gray-300">
                        {{ Number(proposal.dpw || 0) }}d
                      </td>
                      <td class="py-2 px-2 text-right font-semibold text-gray-900 dark:text-white">
                        {{ Number(proposal.hours_this_week || 0).toFixed(1) }}u
                      </td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr class="border-t-2 border-blue-300 dark:border-blue-600">
                      <td
                        colspan="3"
                        class="py-2 px-2 font-semibold text-blue-900 dark:text-blue-200"
                      >
                        Totaal voorgesteld
                      </td>
                      <td class="py-2 px-2 text-right font-bold text-blue-900 dark:text-blue-200">
                        {{ selectedWeekProposedTotal.toFixed(1) }}u
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <!-- Presourcing / Assignments for selected week -->
              <div
                v-if="selectedWeekAssignments.length > 0"
                class="mt-4 pt-4 border-t border-blue-200 dark:border-blue-700"
              >
                <h5
                  class="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-2 flex items-center gap-2"
                >
                  <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                    />
                  </svg>
                  Presourcing deze week
                </h5>
                <table class="min-w-full text-sm">
                  <thead>
                    <tr class="border-b border-blue-200 dark:border-blue-700">
                      <th class="text-left py-2 px-2 font-medium text-blue-900 dark:text-blue-200">
                        Project
                      </th>
                      <th class="text-left py-2 px-2 font-medium text-blue-900 dark:text-blue-200">
                        Rol
                      </th>
                      <th class="text-right py-2 px-2 font-medium text-blue-900 dark:text-blue-200">
                        DPW
                      </th>
                      <th class="text-right py-2 px-2 font-medium text-blue-900 dark:text-blue-200">
                        Uren
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(assignment, idx) in selectedWeekAssignments"
                      :key="idx"
                      class="border-b border-blue-100 dark:border-blue-800 last:border-0"
                    >
                      <td class="py-2 px-2 text-gray-700 dark:text-gray-300">
                        {{ assignment.prospect_subject }}
                        <span
                          v-if="assignment.organization_name"
                          class="text-gray-500 dark:text-gray-400"
                        >
                          - {{ assignment.organization_name }}</span
                        >
                      </td>
                      <td class="py-2 px-2 text-purple-700 dark:text-purple-300">
                        {{ assignment.role || '—' }}
                      </td>
                      <td class="py-2 px-2 text-right text-gray-700 dark:text-gray-300">
                        {{ assignment.dpw }}d
                      </td>
                      <td class="py-2 px-2 text-right font-semibold text-gray-900 dark:text-white">
                        {{ assignment.hours_this_week.toFixed(1) }}u
                      </td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr class="border-t-2 border-blue-300 dark:border-blue-600">
                      <td
                        colspan="3"
                        class="py-2 px-2 font-semibold text-blue-900 dark:text-blue-200"
                      >
                        Totaal presourcing
                      </td>
                      <td class="py-2 px-2 text-right font-bold text-blue-900 dark:text-blue-200">
                        {{ selectedWeekAssignmentsTotal.toFixed(1) }}u
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>

          <!-- Right Column: Running Projects + Pre-sourcings -->
          <!-- sm:min-h-0 is required so the flex item can shrink below its
               content height; without it the overflow-y-auto never kicks in. -->
          <div
            class="w-full sm:w-64 md:w-80 lg:w-96 sm:flex-shrink-0 sm:overflow-y-auto sm:min-h-0"
          >
            <EmployeeProjectsSidebar
              :employee-id="employee?.simplicate_id"
              :period-start="periodStart"
              :period-end="periodEnd"
              :extra-proposals="extraProposals"
            />
          </div>
        </div>
      </div>
      <div
        class="px-6 py-4 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-700 text-right flex-shrink-0"
      >
        <button
          @click="$emit('close')"
          class="px-4 py-2 rounded-lg bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 text-gray-800 dark:text-white"
        >
          Sluiten
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import EmployeeHoursBarChart from '@/components/employee/EmployeeHoursBarChart.vue'
import EmployeeProjectsSidebar from '@/components/employee/EmployeeProjectsSidebar.vue'
import { hoursService } from '@/services/hoursService.js'
import { useScrollLock } from '@/composables/ui/useScrollLock'

const props = defineProps({
  employee: { type: Object, required: true },
  week: { type: Object, required: true },
  periodStart: { type: String, default: null },
  periodEnd: { type: String, default: null },
  extraProposedHoursByWeek: { type: Object, default: () => ({}) },
  extraProposals: { type: Array, default: () => [] }
})

// Scroll lock - lock when mounted, auto-unlocks on unmount via composable
const { lockScroll } = useScrollLock()
lockScroll()

// Selected week state
const selectedWeek = ref(null)
const selectedWeekEntries = ref([])
const loadingSelectedWeek = ref(false)

// Calculate total for selected week
const effectiveHours = row =>
  Number(row.effective_hours ?? Number(row.hours || 0) + Number(row.correction_amount || 0))

const formatSignedHours = value => {
  const hours = Number(value || 0)
  return `${hours > 0 ? '+' : ''}${hours.toFixed(1)}`
}

const selectedWeekTotal = computed(() => {
  return selectedWeekEntries.value.reduce((sum, row) => sum + effectiveHours(row), 0)
})

// Get assignments for selected week from employee.weeks data
const selectedWeekAssignments = computed(() => {
  if (!selectedWeek.value?.week_start || !props.employee?.weeks) return []

  const weekData = props.employee.weeks.find(w => w.week_start === selectedWeek.value.week_start)
  return weekData?.assignments || []
})

// Calculate total assignment hours for selected week
const selectedWeekAssignmentsTotal = computed(() => {
  return selectedWeekAssignments.value.reduce((sum, a) => sum + Number(a.hours_this_week || 0), 0)
})

// Get proposed (voorgesteld) entries for selected week — server-side
// proposed_assignments plus any locally-staged entries injected by the
// caller. Each entry carries kans + rol so the user can see where the
// employee is already proposed.
const selectedWeekProposed = computed(() => {
  if (!selectedWeek.value?.week_start || !props.employee?.weeks) return []
  const weekData = props.employee.weeks.find(w => w.week_start === selectedWeek.value.week_start)
  return weekData?.proposed_assignments || []
})

const selectedWeekProposedTotal = computed(() => {
  return selectedWeekProposed.value.reduce((sum, p) => sum + Number(p.hours_this_week || 0), 0)
})

// Load hours for a specific week
const loadWeekHours = async weekData => {
  if (!weekData || !props.employee?.simplicate_id) return

  selectedWeek.value = weekData
  loadingSelectedWeek.value = true

  try {
    const rows = await hoursService.getHours({
      employee_id: props.employee.simplicate_id,
      start_date: weekData.week_start,
      end_date: weekData.week_end,
      limit: 1000
    })
    selectedWeekEntries.value = Array.isArray(rows)
      ? rows.sort((a, b) => new Date(a.start_date) - new Date(b.start_date))
      : []
  } catch (e) {
    console.error('Failed to load week hours:', e)
    selectedWeekEntries.value = []
  } finally {
    loadingSelectedWeek.value = false
  }
}

// Handle week click from chart
const handleWeekClick = weekData => {
  loadWeekHours(weekData)
}

// Auto-load first week on mount or when week prop changes
const loadInitialWeek = () => {
  // Use the clicked week from Beschikbaarheid or default to first week
  if (props.week?.week_start) {
    loadWeekHours({
      week_start: props.week.week_start,
      week_end: props.week.week_end,
      iso_week: props.week.iso_week || props.week.iso_label
    })
  } else if (props.employee?.weeks?.length > 0) {
    // Fallback to first week in employee data
    const firstWeek = props.employee.weeks[0]
    loadWeekHours({
      week_start: firstWeek.week_start,
      week_end: firstWeek.week_end,
      iso_week: firstWeek.iso_week
    })
  }
}

// Watch for changes and load initial data
watch(
  () => [props.employee?.simplicate_id, props.week?.week_start],
  () => {
    loadInitialWeek()
  },
  { immediate: true }
)

// Color helpers for type labels
// Verlof = green shades, Verzuim = red, Project/Werktijd = blue shades
const LABEL_COLORS = {
  // Project/Werktijd - blue shades
  Projectmanagement: { bg: '#dbeafe', text: '#1e40af' }, // blue-100/blue-800
  Intern: { bg: '#dbeafe', text: '#2563eb' }, // blue-100/blue-600
  'Interne activiteiten': { bg: '#dbeafe', text: '#3b82f6' }, // blue-100/blue-500
  'Interne drempelverlagende uren': { bg: '#dbeafe', text: '#60a5fa' }, // blue-100/blue-400
  'Performance en Expertisetijd': { bg: '#dbeafe', text: '#1d4ed8' }, // blue-100/blue-700
  'Interne uren': { bg: '#dbeafe', text: '#3b82f6' }, // blue-100/blue-500
  Internal: { bg: '#dbeafe', text: '#3b82f6' }, // blue-100/blue-500
  Declarabel: { bg: '#dbeafe', text: '#1d4ed8' },
  Billable: { bg: '#dbeafe', text: '#1d4ed8' },
  'Niet declarabel': { bg: '#f1f5f9', text: '#475569' },
  'Non-billable': { bg: '#f1f5f9', text: '#475569' },

  // Verlof - green shades
  Vakantiedagen: { bg: '#d1fae5', text: '#059669' }, // emerald-100/emerald-600
  Verlof: { bg: '#d1fae5', text: '#059669' }, // emerald-100/emerald-600
  Leave: { bg: '#d1fae5', text: '#059669' }, // emerald-100/emerald-600
  Feestdagenverlof: { bg: '#d1fae5', text: '#047857' }, // emerald-100/emerald-700
  'Tijd voor tijd': { bg: '#d1fae5', text: '#065f46' }, // emerald-100/emerald-800
  'Buitengewoon verlof': { bg: '#d1fae5', text: '#047857' }, // emerald-100/emerald-700
  'Onbetaald verlof': { bg: '#d1fae5', text: '#065f46' }, // emerald-100/emerald-800
  Ouderschapsverlof: { bg: '#dcfce7', text: '#16a34a' }, // green-100/green-600
  Partnerverlof: { bg: '#dcfce7', text: '#15803d' }, // green-100/green-700
  Zwangerschapsverlof: { bg: '#dcfce7', text: '#166534' }, // green-100/green-800
  'zorgverlof (kortdurend)': { bg: '#dcfce7', text: '#166534' }, // green-100/green-800

  // Verzuim - red shades
  Ziek: { bg: '#fee2e2', text: '#dc2626' }, // red-100/red-600
  Sick: { bg: '#fee2e2', text: '#dc2626' }, // red-100/red-600

  // Other
  Training: { bg: '#ede9fe', text: '#7c3aed' },
  Reizen: { bg: '#cffafe', text: '#0891b2' },
  Sales: { bg: '#dcfce7', text: '#16a34a' },
  Overig: { bg: '#f3f4f6', text: '#6b7280' }
}

const getLabelBgColor = label => {
  const key = String(label || 'Overig')
  return LABEL_COLORS[key]?.bg || LABEL_COLORS['Overig'].bg
}

const getLabelTextColor = label => {
  const key = String(label || 'Overig')
  return LABEL_COLORS[key]?.text || LABEL_COLORS['Overig'].text
}

function formatDate(input) {
  if (!input) return ''
  let d
  if (input instanceof Date) {
    d = input
  } else {
    const s = String(input)
    d = s.includes('T') ? new Date(s) : new Date(`${s}T00:00:00`)
  }
  if (isNaN(d)) return String(input)
  return d.toLocaleDateString('nl-NL')
}

function formatDateShort(input) {
  if (!input) return ''
  let d
  if (input instanceof Date) {
    d = input
  } else {
    const s = String(input)
    d = s.includes('T') ? new Date(s) : new Date(`${s}T00:00:00`)
  }
  if (isNaN(d)) return String(input)
  return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

function getSimplicateProjectUrl(projectId) {
  // projectId format: "project:f008474e41689a7aff21ac8d58daad49"
  return `https://frisseblikken.simplicate.nl/projects/${projectId}/services`
}

function formatProjectDisplay(row) {
  const project = row.project_name
  const org = row.organization_name

  if (project && org) {
    return `${project} - ${org}`
  }
  return project || org || row.type_label || '-'
}
</script>
